/**
 * La carga de viajes y el margen en vivo.
 *
 * Es la pantalla que convierte al sistema en una herramienta de decisión: el
 * margen se recalcula con cada cambio, así se puede decir que no a una tarifa
 * ANTES de aceptarla, no a fin de mes cuando ya no se puede hacer nada.
 *
 * El cálculo lo hace el SERVIDOR, no esta pantalla. Dos motivos: la plata se
 * calcula en un solo lugar en todo el sistema, y el cálculo necesita datos
 * que el navegador no tiene (los costos fijos del camión y los km del mes).
 */

import { formatearPesos, formatearPesosCortos, formatearKm, formatearFecha } from './formato.js';

const $ = (sel, dentro = document) => dentro.querySelector(sel);

function alerta(tipo, texto) {
  const simbolos = { critico: '!', aviso: '!', bien: '✓' };
  const div = document.createElement('div');
  div.className = `alerta ${tipo}`;
  div.setAttribute('role', tipo === 'critico' ? 'alert' : 'status');
  div.innerHTML = `<span class="simbolo" aria-hidden="true">${simbolos[tipo] ?? '·'}</span><span></span>`;
  div.lastElementChild.textContent = texto;
  return div;
}

const ESTADOS = {
  planificado: { texto: 'Planificado', clase: '' },
  en_curso:    { texto: 'En curso',    clase: 'aviso' },
  entregado:   { texto: 'Entregado',   clase: 'bien' },
  facturado:   { texto: 'Facturado',   clase: 'bien' },
  cobrado:     { texto: 'Cobrado',     clase: 'bien' },
};

/** Los pesos que se escriben en pantalla, a centavos enteros. */
function aCentavos(texto) {
  const limpio = String(texto ?? '').replace(/\./g, '').replace(',', '.').trim();
  if (limpio === '') return '0';
  if (!/^\d+(\.\d{1,2})?$/.test(limpio)) return null;
  const [entero, decimal = ''] = limpio.split('.');
  return `${entero}${decimal.padEnd(2, '0')}`;
}

const periodoActual = () => {
  const h = new Date();
  return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, '0')}`;
};

// =======================================================================
// Listas para elegir (clientes, camiones, choferes)
// =======================================================================
async function traerListas(pedir) {
  const [clientes, camiones, choferes] = await Promise.all([
    pedir('/api/clientes'), pedir('/api/camiones'), pedir('/api/choferes'),
  ]);
  // Camiones y acoplados salen de la misma lista y se separan acá: en el
  // viaje no se puede elegir un tractor como acoplado ni al revés.
  const vehiculos = camiones.ok ? camiones.cuerpo.camiones : [];
  return {
    clientes: clientes.ok ? clientes.cuerpo.clientes : [],
    vehiculos,
    camiones:  vehiculos.filter((v) => !v.remolcado),
    acoplados: vehiculos.filter((v) => v.remolcado),
    choferes: choferes.ok ? choferes.cuerpo.choferes : [],
  };
}

const opciones = (lista, etiqueta, elegido) =>
  lista.map((f) => `<option value="${f.id}" ${f.id === elegido ? 'selected' : ''}>${
    etiqueta(f)}</option>`).join('');

const nombreCliente = (c) => c.razon_social;
const nombreCamion  = (c) => `${c.patente}${c.marca ? ' — ' + c.marca : ''}`;
const nombreChofer  = (c) => `${c.nombre} ${c.apellido}`;

// =======================================================================
// El panel del margen
// =======================================================================
function dibujarMargen(panel, m, gastosCargados) {
  if (m.falta) {
    panel.innerHTML = `<div class="tarjeta">
      <h2 style="font-size:15px;margin:0 0 6px">Cuánto deja este viaje</h2>
      <p style="color:var(--texto-suave);font-size:13px;margin:0">${m.falta}</p></div>`;
    return;
  }

  const renglon = (rotulo, valor, signo = '') =>
    `<tr><td>${signo} ${rotulo}</td><td class="plata">${
      signo === '−' ? '−' : ''} ${formatearPesos(valor)}</td></tr>`;

  const margenTexto = (Number(m.margenPorcentaje) / 10000).toFixed(1).replace('.', ',');
  const pierde = BigInt(m.gananciaCents) < 0n;
  const bajo = m.alertas?.some((a) => a.codigo === 'margen_bajo');

  const avisos = (m.alertas ?? []).map((a) =>
    `<div class="alerta ${a.gravedad === 'freno' ? 'critico' : 'aviso'}" role="status">
       <span class="simbolo" aria-hidden="true">!</span><span>${a.texto}</span></div>`).join('');

  panel.innerHTML = `<div class="tarjeta">
    <h2 style="font-size:15px;margin:0 0 4px">Cuánto deja este viaje</h2>
    <p style="color:var(--texto-suave);font-size:12.5px;margin:0 0 14px">
      Se recalcula solo, con cada cambio.${gastosCargados !== undefined
        ? ` Con ${gastosCargados} gasto(s) cargado(s).` : ' Los gastos son estimados.'}</p>
    ${avisos}
    <div class="tabla-scroll"><table class="datos" style="min-width:0">
      <tbody>
        ${renglon('Flete neto (sin IVA)', m.fleteNetoCents)}
        <tr style="color:var(--texto-suave)"><td>IVA (se factura aparte)</td>
            <td class="plata">${formatearPesos(m.ivaCents)}</td></tr>
        ${BigInt(m.gastosCents) > 0n ? renglon('Gastos del viaje', m.gastosCents, '−') : ''}
        ${BigInt(m.comisionDadorCents) > 0n ? renglon('Comisión del dador', m.comisionDadorCents, '−') : ''}
        ${renglon('Chofer (sobre el flete completo)', m.choferCents, '−')}
        ${renglon('Costos fijos prorrateados', m.prorrateoCents, '−')}
        ${(m.prorrateoDetalle ?? []).length > 1 ? m.prorrateoDetalle.map((d) => `
        <tr style="color:var(--texto-suave);font-size:12.5px">
          <td style="padding-left:18px">${d.vehiculo}</td>
          <td class="plata">${formatearPesos(d.cents)}</td></tr>`).join('') : ''}
      </tbody>
      <tfoot>
        <tr><td><b>Ganancia del viaje</b></td>
            <td class="plata" data-rol="ganancia" style="color:${
              pierde ? 'var(--critico)' : bajo ? 'var(--grave)' : 'var(--bien-texto)'}">
              <b>${formatearPesos(m.gananciaCents)}</b></td></tr>
        <tr><td>Margen</td><td class="plata" data-rol="margen" style="color:${
          pierde ? 'var(--critico)' : bajo ? 'var(--grave)' : 'var(--bien-texto)'}">
          ${margenTexto} %</td></tr>
        <tr style="color:var(--texto-suave)"><td>Costo por km</td>
            <td class="plata">${formatearPesos(m.costoPorKmCents)}</td></tr>
        <tr style="color:var(--texto-suave)"><td>Ingreso por km</td>
            <td class="plata">${formatearPesos(m.ingresoPorKmCents)}</td></tr>
      </tfoot>
    </table></div>
  </div>`;
}

// =======================================================================
// Nuevo viaje
// =======================================================================
export async function pantallaDeNuevoViaje(contenedor, { pedir, alGuardar }) {
  const listas = await traerListas(pedir);

  if (listas.clientes.length === 0 || listas.camiones.length === 0 || listas.choferes.length === 0) {
    contenedor.appendChild(alerta('aviso',
      'Antes de cargar un viaje hace falta tener al menos un cliente, un camión y un chofer. ' +
      'Cargalos en «Datos base».'));
    return;
  }

  const marco = document.createElement('div');
  marco.style.cssText = 'display:grid;grid-template-columns:minmax(0,1.3fr) minmax(0,1fr);gap:18px;align-items:start';
  marco.innerHTML = `
    <div class="tarjeta">
      <h2 style="font-size:16px;margin:0 0 4px">Datos del viaje</h2>
      <p style="color:var(--texto-suave);font-size:13px;margin:0 0 16px">
        El número se asigna solo. Los campos con * son obligatorios.</p>
      <div data-rol="errores"></div>
      <!-- method="post": si el JavaScript no carga, el navegador no puede
           mandar los datos por la barra de direcciones. -->
      <form method="post" action="/api/viajes" novalidate id="formulario-viaje">
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:0 16px">
          <div class="campo"><label for="v-cliente">Cliente *</label>
            <select id="v-cliente" name="cliente_id"><option value="">— elegir —</option>
              ${opciones(listas.clientes, nombreCliente)}</select></div>
          <div class="campo"><label for="v-orden">Orden de carga del cliente</label>
            <input type="text" id="v-orden" name="orden_carga_cliente"></div>
          <div class="campo"><label for="v-origen">Origen *</label>
            <input type="text" id="v-origen" name="origen"></div>
          <div class="campo"><label for="v-destino">Destino *</label>
            <input type="text" id="v-destino" name="destino"></div>
          <div class="campo"><label for="v-chofer">Chofer *</label>
            <select id="v-chofer" name="chofer_id"><option value="">— elegir —</option>
              ${opciones(listas.choferes, nombreChofer)}</select>
            <div class="ayuda">Al elegirlo se precarga su camión habitual.</div></div>
          <div class="campo"><label for="v-camion">Camión *</label>
            <select id="v-camion" name="camion_id"><option value="">— elegir —</option>
              ${opciones(listas.camiones, nombreCamion)}</select>
            <div class="ayuda" data-rol="aviso-camion"></div></div>
          <div class="campo"><label for="v-acoplado">Acoplado</label>
            <select id="v-acoplado" name="acoplado_id"><option value="">— sin acoplado —</option>
              ${opciones(listas.acoplados, nombreCamion)}</select>
            <div class="ayuda" data-rol="aviso-acoplado">${listas.acoplados.length === 0
              ? 'Todavía no hay acoplados cargados. Se cargan en Camiones, tildando «Es un acoplado».'
              : 'Sus costos fijos también entran en la cuenta del viaje.'}</div></div>
          <div class="campo"><label for="v-salida">Fecha de salida</label>
            <input type="date" id="v-salida" name="fecha_salida"></div>
          <div class="campo"><label for="v-fin">Fecha de fin</label>
            <input type="date" id="v-fin" name="fecha_fin">
            <div class="ayuda">Define en qué mes se le liquida al chofer.</div></div>
          <div class="campo"><label for="v-km">Km recorridos</label>
            <input type="text" id="v-km" name="km_recorridos" inputmode="decimal">
            <div class="ayuda">Un decimal. Se usa para el costo por km.</div></div>
          <div class="campo"><label for="v-flete">Flete neto, sin IVA ($) *</label>
            <input type="text" id="v-flete" name="flete_neto_cents" inputmode="decimal">
            <div class="ayuda">Es la base del porcentaje del chofer.</div></div>
          <div class="campo"><label for="v-comision">Comisión del dador ($)</label>
            <input type="text" id="v-comision" name="comision_dador_cents" inputmode="decimal"></div>
          <div class="campo"><label for="v-estado">Estado</label>
            <select id="v-estado" name="estado">
              <option value="planificado" selected>Planificado</option>
              <option value="en_curso">En curso</option>
              <option value="entregado">Entregado</option>
            </select></div>
        </div>

        <h3 style="font-size:14px;margin:16px 0 8px">Gastos estimados del viaje</h3>
        <p style="color:var(--texto-suave);font-size:12.5px;margin:0 0 10px">
          Son sólo para ver el margen ahora. Los gastos de verdad se cargan después,
          con su ticket.</p>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:0 16px">
          <div class="campo"><label for="g-combustible">Combustible ($ neto)</label>
            <input type="text" id="g-combustible" inputmode="decimal" data-gasto="Combustible"></div>
          <div class="campo"><label for="g-peajes">Peajes ($ neto)</label>
            <input type="text" id="g-peajes" inputmode="decimal" data-gasto="Peajes"></div>
          <div class="campo"><label for="g-otros">Otros de ruta ($ neto)</label>
            <input type="text" id="g-otros" inputmode="decimal" data-gasto="Otros de ruta"></div>
        </div>

        <div style="display:flex;gap:10px;margin-top:14px">
          <button type="submit" class="boton principal" id="boton-guardar-viaje">Guardar viaje</button>
          <button type="button" class="boton" data-accion="limpiar">Limpiar</button>
        </div>
      </form>
    </div>
    <div data-rol="panel-margen"></div>`;
  contenedor.appendChild(marco);

  const form = $('#formulario-viaje', marco);
  const panel = $('[data-rol="panel-margen"]', marco);
  const cajaErrores = $('[data-rol="errores"]', marco);
  const avisoCamion = $('[data-rol="aviso-camion"]', marco);
  const avisoAcoplado = $('[data-rol="aviso-acoplado"]', marco);

  /**
   * El acoplado habitual del chasis, precargado.
   *
   * Es una SUGERENCIA, igual que el camión habitual del chofer: se puede
   * cambiar o dejar vacío. Sólo se pisa lo que haya si el chasis cambió.
   */
  function precargarAcoplado() {
    const camion = listas.camiones.find((c) => c.id === $('#v-camion', marco).value);
    const select = $('#v-acoplado', marco);
    if (!camion?.acoplado_habitual_id) return;
    const acoplado = listas.acoplados.find((a) => a.id === camion.acoplado_habitual_id);
    if (!acoplado) return;
    select.value = acoplado.id;
    avisoAcoplado.textContent =
      `Precargado: es el acoplado habitual de ${camion.patente}. Podés cambiarlo o sacarlo.`;
  }

  dibujarMargen(panel, { falta: 'Elegí el chofer, el camión y el flete para ver el margen.' });

  // --- El camión habitual, precargado ---------------------------------
  $('#v-chofer', marco).addEventListener('change', (e) => {
    const chofer = listas.choferes.find((c) => c.id === e.target.value);
    avisoCamion.textContent = '';
    if (chofer?.camion_habitual_id) {
      const camion = listas.camiones.find((c) => c.id === chofer.camion_habitual_id);
      if (camion) {
        // Es una SUGERENCIA: se puede cambiar sin que el sistema diga nada.
        $('#v-camion', marco).value = chofer.camion_habitual_id;
        avisoCamion.textContent = `Precargado: es el camión habitual de ${chofer.nombre}. Podés cambiarlo.`;
        precargarAcoplado();
      }
    }
    void recalcular();
  });

  // --- El acoplado habitual del chasis, precargado ---------------------
  $('#v-camion', marco).addEventListener('change', () => {
    precargarAcoplado();
    void recalcular();
  });

  // --- El margen en vivo ----------------------------------------------
  let pendiente = null;
  async function recalcular() {
    const datos = new FormData(form);
    const flete = aCentavos(datos.get('flete_neto_cents'));
    const comision = aCentavos(datos.get('comision_dador_cents'));

    if (!datos.get('chofer_id') || !datos.get('camion_id') || flete === null || !flete || flete === '0') {
      dibujarMargen(panel, { falta: 'Elegí el chofer, el camión y el flete para ver el margen.' });
      return;
    }

    const gastos = [...form.querySelectorAll('[data-gasto]')].map((i) => ({
      concepto: i.dataset.gasto, netoCents: aCentavos(i.value) ?? '0',
    })).filter((g) => g.netoCents !== '0');

    const r = await pedir('/api/viajes/margen', { method: 'POST', body: JSON.stringify({
      chofer_id: datos.get('chofer_id'), camion_id: datos.get('camion_id'),
      acoplado_id: datos.get('acoplado_id') || null,
      flete_neto_cents: flete, comision_dador_cents: comision ?? '0',
      km_recorridos: datos.get('km_recorridos'), fecha_fin: datos.get('fecha_fin'),
      iva_porcentaje: '21', gastos,
    }) });
    if (r.ok) dibujarMargen(panel, r.cuerpo);
  }

  // Se espera a que la persona termine de escribir, para no pedirle al
  // servidor una cuenta por cada tecla.
  const recalcularPronto = () => {
    clearTimeout(pendiente);
    pendiente = setTimeout(() => { void recalcular(); }, 350);
  };
  form.addEventListener('input', recalcularPronto);
  form.addEventListener('change', recalcularPronto);

  // --- Guardar ---------------------------------------------------------
  $('[data-accion="limpiar"]', marco).addEventListener('click', () => {
    form.reset();
    avisoCamion.textContent = '';
    cajaErrores.innerHTML = '';
    dibujarMargen(panel, { falta: 'Elegí el chofer, el camión y el flete para ver el margen.' });
  });

  form.addEventListener('submit', async (evento) => {
    evento.preventDefault();
    cajaErrores.innerHTML = '';
    const boton = $('#boton-guardar-viaje', marco);
    boton.disabled = true; boton.textContent = 'Guardando…';

    try {
      const datos = Object.fromEntries(new FormData(form));
      const cuerpo = {
        ...datos,
        flete_neto_cents: aCentavos(datos.flete_neto_cents) ?? '',
        comision_dador_cents: aCentavos(datos.comision_dador_cents) ?? '0',
      };
      const r = await pedir('/api/viajes', { method: 'POST', body: JSON.stringify(cuerpo) });

      if (r.ok) {
        cajaErrores.appendChild(alerta('bien',
          `Viaje ${r.cuerpo.numero} guardado.`));
        form.reset();
        avisoCamion.textContent = '';
        dibujarMargen(panel, { falta: 'Elegí el chofer, el camión y el flete para ver el margen.' });
        if (alGuardar) alGuardar(r.cuerpo);
        return;
      }
      if (r.cuerpo?.errores) {
        for (const e of r.cuerpo.errores) {
          cajaErrores.appendChild(alerta('critico', `${e.rotulo}: ${e.problema}`));
        }
      } else {
        cajaErrores.appendChild(alerta('critico', r.cuerpo?.error ?? 'No se pudo guardar.'));
      }
    } finally {
      boton.disabled = false; boton.textContent = 'Guardar viaje';
    }
  });
}

// =======================================================================
// El listado, con filtros
// =======================================================================
export async function pantallaDeViajes(contenedor, { pedir }) {
  const listas = await traerListas(pedir);

  const filtros = document.createElement('div');
  filtros.className = 'tarjeta';
  filtros.style.marginBottom = '14px';
  filtros.innerHTML = `
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:0 14px">
      <div class="campo"><label for="f-periodo">Mes</label>
        <input type="month" id="f-periodo" value="${periodoActual()}"></div>
      <div class="campo"><label for="f-chofer">Chofer</label>
        <select id="f-chofer"><option value="">Todos</option>
          ${opciones(listas.choferes, nombreChofer)}</select></div>
      <div class="campo"><label for="f-camion">Camión</label>
        <select id="f-camion"><option value="">Todos</option>
          ${opciones(listas.camiones, nombreCamion)}</select></div>
      <div class="campo"><label for="f-acoplado">Acoplado</label>
        <select id="f-acoplado"><option value="">Todos</option>
          ${opciones(listas.acoplados, nombreCamion)}</select></div>
      <div class="campo"><label for="f-cliente">Cliente</label>
        <select id="f-cliente"><option value="">Todos</option>
          ${opciones(listas.clientes, nombreCliente)}</select></div>
      <div class="campo"><label for="f-estado">Estado</label>
        <select id="f-estado"><option value="">Todos</option>
          ${Object.entries(ESTADOS).map(([v, e]) => `<option value="${v}">${e.texto}</option>`).join('')}
        </select></div>
    </div>
    <div style="display:flex;gap:12px;align-items:center;flex-wrap:wrap">
      <label style="display:flex;align-items:center;gap:7px;font-size:13.5px;color:var(--texto-2)">
        <input type="checkbox" id="f-anulados"> Mostrar también los anulados</label>
      <button class="boton" data-accion="limpiar-filtros">Limpiar filtros</button>
      <span style="flex:1"></span>
      <span data-rol="resumen" style="color:var(--texto-suave);font-size:13px"></span>
    </div>`;
  contenedor.appendChild(filtros);

  const tabla = document.createElement('div');
  tabla.className = 'tarjeta';
  contenedor.appendChild(tabla);

  async function dibujar() {
    const params = new URLSearchParams();
    const periodo = $('#f-periodo', filtros).value;
    if (periodo) params.set('periodo', periodo);
    for (const [id, nombre] of [['f-chofer', 'chofer'], ['f-camion', 'camion'],
                                ['f-acoplado', 'acoplado'],
                                ['f-cliente', 'cliente'], ['f-estado', 'estado']]) {
      const v = $(`#${id}`, filtros).value;
      if (v) params.set(nombre, v);
    }
    if ($('#f-anulados', filtros).checked) params.set('anulados', 'si');

    const r = await pedir(`/api/viajes?${params.toString()}`);
    if (!r.ok) {
      tabla.innerHTML = '';
      tabla.appendChild(alerta('critico', r.cuerpo?.error ?? 'No se pudo traer la lista.'));
      return;
    }
    const viajes = r.cuerpo.viajes ?? [];

    const totalFlete = viajes.filter((v) => !v.anulado)
      .reduce((s, v) => s + BigInt(v.flete_neto_cents), 0n);
    const totalKm = viajes.filter((v) => !v.anulado)
      .reduce((s, v) => s + BigInt(Math.round(Number(v.km_recorridos ?? 0) * 10)), 0n);
    $('[data-rol="resumen"]', filtros).textContent =
      `${viajes.length} viaje(s) · ${formatearPesosCortos(totalFlete)} · ${formatearKm(totalKm)} km`;

    if (viajes.length === 0) {
      tabla.innerHTML = `<p style="color:var(--texto-suave);text-align:center;padding:26px 0;margin:0">
        No hay viajes con esos filtros.</p>`;
      return;
    }

    tabla.innerHTML = `<div class="tabla-scroll"><table class="datos">
      <thead><tr>
        <th>Viaje</th><th>Fin</th><th>Cliente</th><th>Recorrido</th>
        <th>Chofer</th><th>Camión</th><th>Acoplado</th>
        <th class="numero">Km</th><th class="plata">Flete neto</th><th>Estado</th>
      </tr></thead>
      <tbody>${viajes.map((v) => `
        <tr data-id="${v.id}" style="${v.anulado ? 'opacity:.55' : ''}">
          <td>${v.numero}${v.orden_carga_cliente
            ? `<br><small style="color:var(--texto-suave)">${v.orden_carga_cliente}</small>` : ''}</td>
          <td>${formatearFecha(v.fecha_fin) || '—'}</td>
          <td>${v.cliente_nombre}</td>
          <td>${v.origen} → ${v.destino}</td>
          <td>${v.chofer_nombre}</td>
          <td>${v.camion_patente}</td>
          <td>${v.acoplado_patente ?? '—'}</td>
          <td class="numero">${v.km_recorridos ? formatearKm(Math.round(Number(v.km_recorridos) * 10)) : '—'}</td>
          <td class="plata">${formatearPesos(v.flete_neto_cents)}</td>
          <td>${v.anulado
            ? '<span class="etiqueta critico">Anulado</span>'
            : `<span class="etiqueta ${ESTADOS[v.estado]?.clase ?? ''}">${
                ESTADOS[v.estado]?.texto ?? v.estado}</span>`}</td>
        </tr>`).join('')}</tbody>
    </table></div>`;
  }

  filtros.addEventListener('change', () => { void dibujar(); });
  $('[data-accion="limpiar-filtros"]', filtros).addEventListener('click', () => {
    for (const id of ['f-chofer', 'f-camion', 'f-acoplado', 'f-cliente', 'f-estado']) $(`#${id}`, filtros).value = '';
    $('#f-anulados', filtros).checked = false;
    void dibujar();
  });

  await dibujar();
}
